import React, { useEffect, useState } from 'react'
import { EntraIdRoleRequestDto } from 'api/kmsapiclient'
import { Trans, useTranslation } from 'react-i18next'

import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography,
} from '@mui/material'

import { isScreenSizeSmall } from '../../components/common/utils'

import {
  EntraIdOperationRole,
  EntraIdOperationRoles,
  EntraIdOperationRoleType,
} from './types'

export type EntraIdRoleOperationsViewProps = {
  selectedEntraIdRoles: EntraIdRoleRequestDto | undefined
  onEntraIdOperationRolesChange: (
    selectedEntraIdRoles: EntraIdRoleRequestDto,
  ) => void
}

export const EntraIdOperationRolesView = ({
  selectedEntraIdRoles,
  onEntraIdOperationRolesChange,
}: EntraIdRoleOperationsViewProps) => {
  const { t } = useTranslation()
  const entraIdRoleKeyOperations = EntraIdOperationRoles().filter(
    (operation) => operation.operationType === EntraIdOperationRoleType.Key,
  )

  const entraIdRolePermissionOperations = EntraIdOperationRoles().filter(
    (operation) =>
      operation.operationType === EntraIdOperationRoleType.Permission,
  )

  const entraIdRoleAdministrationOperations = EntraIdOperationRoles().filter(
    (operation) =>
      operation.operationType === EntraIdOperationRoleType.Administration,
  )

  const entraIdRoleSecretOperations = EntraIdOperationRoles().filter(
    (operation) => operation.operationType === EntraIdOperationRoleType.Secret,
  )

  const responsiveFontStyle = {
    fontSize: isScreenSizeSmall() ? 'var(--small-screen-font-size)' : 'inherit',
    whiteSpace: 'normal',
    wordBreak: 'break-all',
  }

  const [selectedEntraIdRolesState, setSelectedEntraIdRolesState] = useState<
    EntraIdRoleRequestDto | undefined
  >(undefined)

  useEffect(() => {
    setSelectedEntraIdRolesState(selectedEntraIdRoles)
  }, [selectedEntraIdRoles])

  /**
   * Function to create the operation item
   *
   * @param {EntraIdOperationRole} operation represents the operation role
   * @return {*} {JSX.Element} returns the operation item
   */
  const createOperationItem = (operation: EntraIdOperationRole) => {
    return (
      <FormControlLabel
        key={operation.name}
        control={
          <Checkbox
            checked={isChecked(operation)}
            onClick={() => handleSelect(operation)}
            name={`chk-${operation.name}`}
            id={`chk-${operation.name}`}
            sx={{
              '& .MuiSvgIcon-root': {
                fontSize: isScreenSizeSmall()
                  ? 'var(--small-screen-font-size)'
                  : 'inherit',
              },
            }}
          />
        }
        label={operation.description}
        sx={{
          margin: 0,
          typography: 'body2',
          fontWeight: 'bold',
          '& .MuiTypography-root': {
            fontSize: isScreenSizeSmall()
              ? 'var(--small-screen-font-size)'
              : 'inherit',
          },
        }}
      />
    )
  }

  /**
   * Function to handle the selected operation
   * @param {EntraIdOperationRole} selected represents the selected operation role
   */
  const handleSelect = (selected: EntraIdOperationRole) => {
    let updatedEntraIdRoles: EntraIdRoleRequestDto
    if (selectedEntraIdRolesState) {
      switch (selected.name) {
        case 'createKey':
          updatedEntraIdRoles = {
            ...selectedEntraIdRolesState,
            createKey: !selectedEntraIdRolesState.createKey,
          }
          break
        case 'rotateKey':
          updatedEntraIdRoles = {
            ...selectedEntraIdRolesState,
            rotateKey: !selectedEntraIdRolesState.rotateKey,
          }
          break
        case 'getKey':
          updatedEntraIdRoles = {
            ...selectedEntraIdRolesState,
            getKey: !selectedEntraIdRolesState.getKey,
          }
          break
        case 'deleteKey':
          updatedEntraIdRoles = {
            ...selectedEntraIdRolesState,
            deleteKey: !selectedEntraIdRolesState.deleteKey,
          }
          break

        case 'addPermission':
          updatedEntraIdRoles = {
            ...selectedEntraIdRolesState,
            addPermission: !selectedEntraIdRolesState.addPermission,
          }
          break
        case 'updatePermission':
          updatedEntraIdRoles = {
            ...selectedEntraIdRolesState,
            updatePermission: !selectedEntraIdRolesState.updatePermission,
          }
          break
        case 'getPermission':
          updatedEntraIdRoles = {
            ...selectedEntraIdRolesState,
            getPermission: !selectedEntraIdRolesState.getPermission,
          }
          break
        case 'deletePermission':
          updatedEntraIdRoles = {
            ...selectedEntraIdRolesState,
            deletePermission: !selectedEntraIdRolesState.deletePermission,
          }
          break

        case 'entraIDGroupRoles':
          updatedEntraIdRoles = {
            ...selectedEntraIdRolesState,
            entraIDGroupRoles: !selectedEntraIdRolesState.entraIDGroupRoles,
          }
          break

        case 'createSecret':
          updatedEntraIdRoles = {
            ...selectedEntraIdRolesState,
            createSecret: !selectedEntraIdRolesState.createSecret,
          }
          break
        case 'getSecret':
          updatedEntraIdRoles = {
            ...selectedEntraIdRolesState,
            getSecret: !selectedEntraIdRolesState.getSecret,
          }
          break
        case 'deleteSecret':
          updatedEntraIdRoles = {
            ...selectedEntraIdRolesState,
            deleteSecret: !selectedEntraIdRolesState.deleteSecret,
          }
          break
        default:
          updatedEntraIdRoles = selectedEntraIdRolesState
          break
      }
      setSelectedEntraIdRolesState(updatedEntraIdRoles)
      onEntraIdOperationRolesChange(updatedEntraIdRoles)
    }
  }

  /**
   * Function to check if the operation is selected
   * @param {EntraIdOperationRole} operation represents the operation role
   * @return {*}  {boolean} returns true if the operation is selected
   */
  const isChecked = (operation: EntraIdOperationRole): boolean => {
    if (!selectedEntraIdRolesState) {
      return false
    }
    switch (operation.name) {
      case 'createKey':
        return selectedEntraIdRolesState.createKey
      case 'rotateKey':
        return selectedEntraIdRolesState.rotateKey
      case 'getKey':
        return selectedEntraIdRolesState.getKey
      case 'deleteKey':
        return selectedEntraIdRolesState.deleteKey

      case 'addPermission':
        return selectedEntraIdRolesState.addPermission
      case 'updatePermission':
        return selectedEntraIdRolesState.updatePermission
      case 'getPermission':
        return selectedEntraIdRolesState.getPermission
      case 'deletePermission':
        return selectedEntraIdRolesState.deletePermission

      case 'entraIDGroupRoles':
        return selectedEntraIdRolesState.entraIDGroupRoles

      case 'createSecret':
        return selectedEntraIdRolesState.createSecret
      case 'getSecret':
        return selectedEntraIdRolesState.getSecret
      case 'deleteSecret':
        return selectedEntraIdRolesState.deleteSecret

      default:
        return false
    }
  }

  /**
   * Handles the "Select All" checkbox change event for Entra ID roles.
   *
   * When the checkbox is checked, this function sets all operation role properties
   * (such as createKey, rotateKey, getKey, etc.) to true in the selectedEntraIdRolesState.
   * When unchecked, it sets all these properties to false.
   * It then updates the local state and notifies the parent component of the change.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} _ - The change event (unused).
   * @param {boolean} checked - The new checked state of the "Select All" checkbox.
   */
  const handleSelectAllChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    if (!selectedEntraIdRolesState) return
    const updatedEntraIdRoles = {
      ...selectedEntraIdRolesState,
      createKey: checked,
      rotateKey: checked,
      getKey: checked,
      deleteKey: checked,
      addPermission: checked,
      updatePermission: checked,
      getPermission: checked,
      deletePermission: checked,
      entraIDGroupRoles: checked,
      createSecret: checked,
      getSecret: checked,
      deleteSecret: checked,
    }
    setSelectedEntraIdRolesState(updatedEntraIdRoles)
    onEntraIdOperationRolesChange(updatedEntraIdRoles)
  }

  return (
    <>
      {selectedEntraIdRolesState && (
        <Grid
          container
          spacing={2}
          direction="row"
          sx={{
            height: '100%',
            maxHeight: '100%',
            overflowY: 'auto',
          }}
        >
          <Grid item xs={12}>
            <Typography
              variant="h6"
              className="bold-text"
              sx={{ fontWeight: 'bold', ...responsiveFontStyle }}
            >
              {t('entraid.roles') +
                ' ' +
                `${selectedEntraIdRolesState.entraIdName}`}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" sx={responsiveFontStyle}>
              <Trans
                i18nKey="entraid.select"
                values={{ entraid: selectedEntraIdRolesState.entraIdName }}
                components={{
                  underline: <span className="bold-text-underline" />,
                }}
              />{' '}
              {t('entraid.title')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    entraIdRoleKeyOperations.every(isChecked) &&
                    entraIdRolePermissionOperations.every(isChecked) &&
                    entraIdRoleAdministrationOperations.every(isChecked) &&
                    entraIdRoleSecretOperations.every(isChecked)
                  }
                  onChange={handleSelectAllChange}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: isScreenSizeSmall()
                        ? 'var(--small-screen-font-size)'
                        : 'inherit',
                    },
                  }}
                />
              }
              label={t('entraid.select_all')}
              componentsProps={{
                typography: { fontWeight: 'bold', ...responsiveFontStyle },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={4}>
              <Grid item xs={isScreenSizeSmall() ? 12 : 6}>
                <Typography
                  variant="subtitle2"
                  className="bold-text"
                  sx={{ fontWeight: 'bold', ...responsiveFontStyle }}
                >
                  {t('entraid.key_operation_role')}
                </Typography>
                <FormGroup key="key-operations-roles">
                  {entraIdRoleKeyOperations.map((operation) =>
                    createOperationItem(operation),
                  )}
                </FormGroup>
              </Grid>
              <Grid item xs={isScreenSizeSmall() ? 12 : 6}>
                <Typography
                  variant="subtitle2"
                  className="bold-text"
                  sx={{ fontWeight: 'bold', ...responsiveFontStyle }}
                >
                  {t('entraid.permission_operation_role')}
                </Typography>
                <FormGroup key="permission-operation-roles">
                  {entraIdRolePermissionOperations.map((operation) =>
                    createOperationItem(operation),
                  )}
                </FormGroup>
              </Grid>
              <Grid item xs={isScreenSizeSmall() ? 12 : 6}>
                <Typography
                  variant="subtitle2"
                  className="bold-text"
                  sx={{ fontWeight: 'bold', ...responsiveFontStyle }}
                >
                  {t('entraid.admin_role')}
                </Typography>
                <FormGroup key="admin-operation-roles">
                  {entraIdRoleAdministrationOperations.map((operation) =>
                    createOperationItem(operation),
                  )}
                </FormGroup>
              </Grid>
              <Grid item xs={isScreenSizeSmall() ? 12 : 6}>
                <Typography
                  variant="subtitle2"
                  className="bold-text"
                  sx={{ fontWeight: 'bold', ...responsiveFontStyle }}
                >
                  {t('entraid.secret_operation_role')}
                </Typography>
                <FormGroup key="secret-operation-roles">
                  {entraIdRoleSecretOperations.map((operation) =>
                    createOperationItem(operation),
                  )}
                </FormGroup>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  )
}
